import React, { useEffect, useRef, useState } from 'react';
import { aplicarEnDocumento } from '../../../utils/diseno/aplicar';
import { parseFormato } from '../../../utils/diseno/slots';
import { AJUSTE_IMAGEN_INICIAL, type AjusteImagen, type BrandPalette } from '../../../utils/diseno/types';

interface Props {
  html: string;
  formato: string;
  slots: Record<string, string>;
  palette: BrandPalette | null;
  ajustes: Record<string, AjusteImagen>;
  ancho: number;
  titulo: string;
  interactivo?: boolean;
  activo?: string | null;
  onDocumento?: (doc: Document | null) => void;
  onSeleccionar?: (slot: string) => void;
  onMoverImagen?: (slot: string, x: number, y: number) => void;
  onZoomImagen?: (slot: string, zoom: number) => void;
}

const ESTILO_INTERACTIVO = `
html,body{cursor:grab;user-select:none}
.scrim-top,.scrim-bottom{pointer-events:none}
[data-slot]{cursor:pointer}
[data-slot] img{cursor:grab;touch-action:none}
[data-slot].clientelo-arrastrando,[data-slot].clientelo-arrastrando img{cursor:grabbing}
.clientelo-slot-activo{outline:4px solid #1A56DB;outline-offset:2px}
`;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

type Punto = { x: number; y: number };

function dosPuntos(puntos: Map<number, Punto>): [Punto, Punto] | null {
  const it = puntos.values();
  const primero = it.next();
  const segundo = it.next();
  if (primero.done || segundo.done) return null;
  return [primero.value, segundo.value];
}

export function MarcoPlantilla({
  html,
  formato,
  slots,
  palette,
  ajustes,
  ancho,
  titulo,
  interactivo,
  activo,
  onDocumento,
  onSeleccionar,
  onMoverImagen,
  onZoomImagen,
}: Props) {
  const ref = useRef<HTMLIFrameElement>(null);
  const alListo = useRef(onDocumento);
  alListo.current = onDocumento;
  const ajustesRef = useRef(ajustes);
  ajustesRef.current = ajustes;
  const seleccionarRef = useRef(onSeleccionar);
  seleccionarRef.current = onSeleccionar;
  const moverRef = useRef(onMoverImagen);
  moverRef.current = onMoverImagen;
  const zoomRef = useRef(onZoomImagen);
  zoomRef.current = onZoomImagen;
  const { width, height } = parseFormato(formato);
  const escala = ancho > 0 ? ancho / width : 0.25;
  const [listo, setListo] = useState(0);

  useEffect(() => {
    const doc = ref.current?.contentDocument;
    if (!doc?.body) return;
    if (interactivo && !doc.getElementById('clientelo-slot-style')) {
      const style = doc.createElement('style');
      style.id = 'clientelo-slot-style';
      style.textContent = ESTILO_INTERACTIVO;
      doc.head.appendChild(style);
    }
    aplicarEnDocumento(doc, slots, palette, ajustes, activo);
    alListo.current?.(doc);
  }, [slots, palette, ajustes, listo, html, interactivo, activo]);

  useEffect(() => {
    if (!interactivo) return;
    const doc = ref.current?.contentDocument;
    if (!doc) return;

    let drag: {
      name: string;
      x0: number;
      y0: number;
      ax: number;
      ay: number;
      w: number;
      h: number;
      moved: boolean;
      el: HTMLElement;
    } | null = null;
    const punteros = new Map<number, { x: number; y: number }>();
    let pellizco: { name: string; dist: number; zoom: number } | null = null;

    const distancia = () => {
      const par = dosPuntos(punteros);
      if (!par) return 0;
      const [a, b] = par;
      return Math.hypot(a.x - b.x, a.y - b.y);
    };

    const slotEn = (x: number, y: number): HTMLElement | null => {
      const pila = doc.elementsFromPoint(x, y);
      for (const node of pila) {
        if (node.nodeType === 1 && (node as Element).hasAttribute('data-slot')) return node as HTMLElement;
      }
      return null;
    };

    const down = (e: PointerEvent) => {
      punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (punteros.size >= 2) {
        drag = null;
        const par = dosPuntos(punteros);
        if (!par) return;
        const [a, b] = par;
        const el = slotEn((a.x + b.x) / 2, (a.y + b.y) / 2);
        const name = el?.getAttribute('data-slot');
        const img = el?.querySelector('img');
        if (name && img?.getAttribute('src')) {
          const aj = ajustesRef.current[name] || AJUSTE_IMAGEN_INICIAL;
          pellizco = { name, dist: Math.max(distancia(), 1), zoom: aj.zoom };
        }
        return;
      }
      const el = slotEn(e.clientX, e.clientY);
      const name = el?.getAttribute('data-slot');
      if (!el || !name) return;
      const img = el.querySelector('img');
      const src = img?.getAttribute('src') || '';
      if (!img || !src) return;
      const rect = el.getBoundingClientRect();
      const aj = ajustesRef.current[name] || AJUSTE_IMAGEN_INICIAL;
      drag = {
        name,
        x0: e.clientX,
        y0: e.clientY,
        ax: aj.x,
        ay: aj.y,
        w: Math.max(rect.width, 1),
        h: Math.max(rect.height, 1),
        moved: false,
        el,
      };
      el.classList.add('clientelo-arrastrando');
      try {
        doc.documentElement.setPointerCapture(e.pointerId);
      } catch {
        /* el iframe igual recibe move/up */
      }
    };

    const move = (e: PointerEvent) => {
      if (punteros.has(e.pointerId)) punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pellizco && punteros.size >= 2) {
        e.preventDefault();
        const zoom = clamp(pellizco.zoom * (distancia() / pellizco.dist), 1, 2.5);
        zoomRef.current?.(pellizco.name, zoom);
        return;
      }
      if (!drag) return;
      const dx = e.clientX - drag.x0;
      const dy = e.clientY - drag.y0;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      if (!drag.moved) return;
      e.preventDefault();
      const x = clamp(drag.ax - (dx / drag.w) * 100, 0, 100);
      const y = clamp(drag.ay - (dy / drag.h) * 100, 0, 100);
      moverRef.current?.(drag.name, x, y);
    };

    const up = (e: PointerEvent) => {
      punteros.delete(e.pointerId);
      if (punteros.size < 2) pellizco = null;
      if (!drag) return;
      drag.el.classList.remove('clientelo-arrastrando');
      const { name, moved } = drag;
      drag = null;
      if (!moved) seleccionarRef.current?.(name);
    };

    const click = (e: MouseEvent) => {
      const el = slotEn(e.clientX, e.clientY);
      const name = el?.getAttribute('data-slot');
      if (!name) return;
      const img = el?.querySelector('img');
      if (img?.getAttribute('src')) return;
      e.preventDefault();
      seleccionarRef.current?.(name);
    };

    const wheel = (e: WheelEvent) => {
      const el = slotEn(e.clientX, e.clientY);
      const name = el?.getAttribute('data-slot');
      const img = el?.querySelector('img');
      if (!el || !name || !img?.getAttribute('src')) return;
      e.preventDefault();
      const aj = ajustesRef.current[name] || AJUSTE_IMAGEN_INICIAL;
      const zoom = clamp(aj.zoom + (e.deltaY < 0 ? 0.08 : -0.08), 1, 2.5);
      zoomRef.current?.(name, zoom);
    };

    doc.addEventListener('pointerdown', down);
    doc.addEventListener('pointermove', move);
    doc.addEventListener('pointerup', up);
    doc.addEventListener('pointercancel', up);
    doc.addEventListener('click', click);
    doc.addEventListener('wheel', wheel, { passive: false });
    return () => {
      doc.removeEventListener('pointerdown', down);
      doc.removeEventListener('pointermove', move);
      doc.removeEventListener('pointerup', up);
      doc.removeEventListener('pointercancel', up);
      doc.removeEventListener('click', click);
      doc.removeEventListener('wheel', wheel);
    };
  }, [interactivo, listo, html]);

  return (
    <div style={{ width: ancho, height: Math.round(height * escala), overflow: 'hidden' }}>
      <iframe
        ref={ref}
        title={titulo}
        srcDoc={html}
        scrolling="no"
        onLoad={() => setListo((n) => n + 1)}
        style={{
          width,
          height,
          border: 0,
          transform: `scale(${escala})`,
          transformOrigin: 'top left',
          pointerEvents: interactivo ? 'auto' : 'none',
          touchAction: interactivo ? 'none' : undefined,
          background: '#fff',
        }}
      />
    </div>
  );
}
